import React, { useEffect, useState } from "react";
import Modal from "./Modal";
import Button from "./Button";

/**
 * The one confirm dialog (audit 7.5). For money-moving actions pass
 * `typeToConfirm` (an amount or a word): the confirm button stays disabled
 * until the user types it, so nothing irreversible happens on a stray click.
 */
export default function ConfirmDialog({
  open,
  title = "Are you sure?",
  message,
  details, // optional [{label, value}] rows, e.g. amount / recipient
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "primary", // primary | danger | warning
  typeToConfirm,
  busy = false,
  hideCancel = false,
  confirmVariant,
  onConfirm,
  onCancel,
}) {
  const [typed, setTyped] = useState("");
  useEffect(() => {
    if (open) setTyped("");
  }, [open]);

  const needsTyping = typeToConfirm != null && String(typeToConfirm) !== "";
  const okTyped = !needsTyping || typed.trim().toLowerCase() === String(typeToConfirm).trim().toLowerCase();
  const icon =
    tone === "danger" ? "fa-exclamation-triangle" : tone === "warning" ? "fa-exclamation-circle" : tone === "success" ? "fa-check-circle" : "fa-question-circle";

  return (
    <Modal
      open={open}
      onClose={onCancel}
      size="sm"
      hideClose
      tone={tone === "primary" ? undefined : tone}
      closeOnOverlay={!busy}
      footer={
        <>
          {hideCancel ? null : (
            <Button variant="secondary" onClick={onCancel} disabled={busy}>
              {cancelLabel}
            </Button>
          )}
          <Button
            variant={confirmVariant || (tone === "danger" ? "danger" : "primary")}
            onClick={onConfirm}
            disabled={!okTyped}
            loading={busy}
            data-autofocus={needsTyping ? undefined : true}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="ee-confirm">
        <span className={`ee-confirm__icon ee-confirm__icon--${tone}`} aria-hidden="true">
          <i className={`fas ${icon}`} />
        </span>
        <div className="ee-confirm__text">
          <h3 className="ee-modal__title" id="ee-modal-title">
            {title}
          </h3>
          {message ? <p className="ee-confirm__msg">{message}</p> : null}
          {Array.isArray(details) && details.length > 0 ? (
            <dl className="ee-confirm__details">
              {details.map((d, i) => (
                <div key={i}>
                  <dt>{d.label}</dt>
                  <dd className="eex-num">{d.value}</dd>
                </div>
              ))}
            </dl>
          ) : null}
          {needsTyping ? (
            <label className="ee-field ee-confirm__type">
              <span className="ee-field__label">
                Type <strong className="eex-num">{String(typeToConfirm)}</strong> to confirm
              </span>
              <input
                className="form-control"
                data-autofocus
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                autoComplete="off"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && okTyped) onConfirm && onConfirm();
                }}
              />
            </label>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}
